import React, { useState, useRef } from 'react';
import { productDisplayNo } from '../utils/productDisplayId';

const emptyEntry = (nextIndex = 1, defaultLogicId = '') => ({
    _key: `entry_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    number: nextIndex,
    name: '',
    imageUrl: '',
    imagePreview: null,
    imageBase64: null,
    link: '',
    logicId: defaultLogicId,
});

export default function ManualProductModal({ isOpen, onClose, onSubmit, nextProductIndex = 1, spaceName = '', logicOptions = [], defaultLogicId = '', isLogicLocked = false, systemDefaultLogicName = 'TEKNIK AI8SAAT' }) {
    const [entries, setEntries] = useState([emptyEntry(nextProductIndex, defaultLogicId)]);
    const fileInputRefs = useRef({});

    if (!isOpen) return null;

    const updateEntry = (key, field, value) => {
        setEntries(prev => prev.map(e => e._key === key ? { ...e, [field]: value } : e));
    };

    const removeEntry = (key) => {
        if (entries.length <= 1) return;
        setEntries(prev => prev.filter(e => e._key !== key));
    };

    const addEntry = () => {
        const maxNum = Math.max(...entries.map(e => e.number || 0), nextProductIndex - 1);
        // Use the last entry's logicId as default for new entries
        const lastLogicId = entries[entries.length - 1]?.logicId || defaultLogicId;
        setEntries(prev => [...prev, emptyEntry(maxNum + 1, lastLogicId)]);
    };

    const handleImageUpload = (key, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setEntries(prev => prev.map(e => e._key === key ? {
                ...e,
                imageBase64: ev.target.result,
                imagePreview: ev.target.result,
                imageUrl: '',
            } : e));
        };
        reader.readAsDataURL(file);
    };

    const handleImageUrlChange = (key, url) => {
        setEntries(prev => prev.map(e => e._key === key ? {
            ...e,
            imageUrl: url,
            imagePreview: url.startsWith('http') ? url : null,
            imageBase64: null,
        } : e));
    };

    const handleSubmit = () => {
        const valid = entries.filter(e => e.name.trim());
        if (valid.length === 0) return;
        onSubmit(valid.map(e => ({
            number: e.number,
            name: e.name.trim(),
            imageUrl: e.imageUrl || '',
            imageBase64: e.imageBase64 || null,
            link: e.link.trim(),
            logicId: e.logicId || defaultLogicId,
        })));
        setEntries([emptyEntry(nextProductIndex, defaultLogicId)]);
        onClose();
    };

    const handleClose = () => {
        setEntries([emptyEntry(nextProductIndex, defaultLogicId)]);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-xl max-h-[90vh] mx-4 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col animate-[slideUp_0.3s_ease-out]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <span className="material-symbols-outlined text-white text-[18px]">edit_note</span>
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-slate-800 dark:text-white">Tambah Produk Manual</h2>
                            <p className="text-[10px] text-slate-400 font-medium">{spaceName ? `Space: ${spaceName}` : 'Masukkan data produk secara manual'}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Entries List (scrollable) */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    {entries.map((entry, idx) => (
                        <div
                            key={entry._key}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg rounded-2xl overflow-hidden relative group/card"
                        >
                            {/* Card Header - matches product card style */}
                            <div className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-800">
                                {/* Image thumbnail */}
                                <div
                                    className="group/thumb relative w-16 aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 cursor-pointer"
                                    onClick={() => fileInputRefs.current[entry._key]?.click()}
                                >
                                    {(entry.imagePreview) ? (
                                        <img alt="thumb" className="w-full h-full object-cover" src={entry.imagePreview} />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-0.5">
                                            <span className="material-symbols-outlined text-xl text-slate-300 group-hover/thumb:text-primary transition-colors">add_photo_alternate</span>
                                            <span className="text-[8px] text-slate-300 font-bold uppercase group-hover/thumb:text-primary transition-colors">Upload</span>
                                        </div>
                                    )}
                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                        <span className="material-symbols-outlined text-white text-[18px]">upload</span>
                                    </div>
                                    <input
                                        ref={el => fileInputRefs.current[entry._key] = el}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => handleImageUpload(entry._key, e.target.files[0])}
                                    />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <span className="text-[12px] font-bold text-indigo-500 whitespace-nowrap tabular-nums" title="ID produk TikTok">#{productDisplayNo(entry, idx + 1)}</span>
                                        {entries.length > 1 && (
                                            <button
                                                onClick={() => removeEntry(entry._key)}
                                                className="ml-auto p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-300 hover:text-red-500 transition-colors"
                                                title="Buang entry ini"
                                            >
                                                <span className="material-symbols-outlined text-[14px]">close</span>
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        type="text"
                                        value={entry.name}
                                        onChange={(e) => updateEntry(entry._key, 'name', e.target.value)}
                                        placeholder="Nama produk..."
                                        className="w-full text-xs font-bold text-slate-800 dark:text-slate-100 uppercase leading-snug bg-transparent border-none focus:outline-none focus:ring-0 placeholder:text-slate-300 placeholder:normal-case p-0"
                                    />
                                </div>
                            </div>

                            {/* Fields */}
                            <div className="p-4 space-y-3">
                                {/* Number + Name row */}
                                <div className="grid grid-cols-[80px_1fr] gap-3">
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">No.</label>
                                        <input
                                            type="number"
                                            value={entry.number}
                                            onChange={(e) => updateEntry(entry._key, 'number', parseInt(e.target.value) || 0)}
                                            className="w-full h-8 px-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Nama Produk</label>
                                        <input
                                            type="text"
                                            value={entry.name}
                                            onChange={(e) => updateEntry(entry._key, 'name', e.target.value)}
                                            placeholder="Cth: Baju Kurung Moden"
                                            className="w-full h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Image URL */}
                                <div>
                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">URL Gambar</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={entry.imageUrl}
                                            onChange={(e) => handleImageUrlChange(entry._key, e.target.value)}
                                            placeholder="https://... atau klik thumbnail untuk upload"
                                            className="flex-1 h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all"
                                        />
                                        <button
                                            onClick={() => fileInputRefs.current[entry._key]?.click()}
                                            className="shrink-0 h-8 px-2.5 flex items-center gap-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 hover:text-primary transition-all text-[10px] font-bold"
                                            title="Upload gambar"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">upload</span>
                                            Upload
                                        </button>
                                    </div>
                                </div>

                                {/* TikTok Link */}
                                <div>
                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Link TikTok</label>
                                    <div className="flex items-center gap-2">
                                        {/* Full link input */}
                                        <div className="flex items-center flex-1 min-w-0">
                                            <span className="flex items-center h-8 px-2 bg-slate-100 dark:bg-slate-800 border border-r-0 border-slate-200 dark:border-slate-700 rounded-l-lg shrink-0">
                                                <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.76a8.12 8.12 0 004.77 1.52V6.84a4.83 4.83 0 01-1-.15z" />
                                                </svg>
                                            </span>
                                            <input
                                                type="text"
                                                value={entry.link}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    updateEntry(entry._key, 'link', val);
                                                }}
                                                placeholder="https://www.tiktok.com/@shop/product/..."
                                                className="flex-1 h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all min-w-0"
                                            />
                                        </div>
                                        {/* Product ID shortcut input */}
                                        <div className="flex items-center shrink-0">
                                            <span className="flex items-center h-8 px-2 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 border border-r-0 border-rose-200 dark:border-rose-800/50 rounded-l-lg" title="Masukkan Product ID sahaja">
                                                <span className="material-symbols-outlined text-[13px] text-rose-400" style={{ fontVariationSettings: "'FILL' 1" }}>tag</span>
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Product ID"
                                                value={(() => {
                                                    // Extract ID from link if it's a tiktok shop link
                                                    const match = entry.link?.match(/product\/(\d+)/);
                                                    return match ? match[1] : '';
                                                })()}
                                                onChange={(e) => {
                                                    const id = e.target.value.replace(/\D/g, ''); // numbers only
                                                    if (id) {
                                                        updateEntry(entry._key, 'link', `https://shop.tiktok.com/view/product/${id}?region=MY&locale=en-US&source=agency`);
                                                    } else {
                                                        updateEntry(entry._key, 'link', '');
                                                    }
                                                }}
                                                className="w-[110px] h-8 px-2 text-xs font-bold text-rose-600 dark:text-rose-400 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-rose-300/50 focus:border-rose-400 placeholder:text-rose-300 dark:placeholder:text-rose-700 placeholder:font-medium transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* AI Logic Selector — show when logic options available OR when locked (client inherits default) */}
                                {(logicOptions.length > 0 || isLogicLocked) && (
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[10px]">neurology</span>
                                            AI Logic
                                        </label>
                                        <div className="relative">
                                            <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] text-violet-400 pointer-events-none">neurology</span>
                                            <select
                                                value={entry.logicId}
                                                onChange={(e) => updateEntry(entry._key, 'logicId', e.target.value)}
                                                className="w-full h-8 pl-8 pr-8 text-xs font-medium text-slate-700 dark:text-slate-200 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300/50 focus:border-violet-400 transition-all appearance-none cursor-pointer"
                                            >
                                                {isLogicLocked ? (
                                                    /* CLIENT on AI8SAAT: limited options — default logic + Data Sahaja */
                                                    <>
                                                        {defaultLogicId && defaultLogicId !== '__data_only__' && (
                                                            <option value={defaultLogicId}>
                                                                {logicOptions.find(opt => opt.id === defaultLogicId)?.name || systemDefaultLogicName}
                                                            </option>
                                                        )}
                                                        <option value="__data_only__">Data Sahaja (Tanpa Prompt)</option>
                                                    </>
                                                ) : (
                                                    /* ADMIN: full options */
                                                    <>
                                                        <option value="__data_only__">Data Sahaja (Tanpa Prompt)</option>
                                                        {logicOptions.map(opt => (
                                                            <option key={opt.id} value={opt.id}>{opt.name || opt.id}</option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-[14px] text-slate-400 pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                    <button
                        onClick={addEntry}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-primary hover:bg-primary/10 border border-dashed border-primary/30 hover:border-primary/60 transition-all"
                    >
                        <span className="material-symbols-outlined text-[16px]">add</span>
                        Tambah Produk Lagi
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            Batal
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!entries.some(e => e.name.trim())}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-[11px] font-bold shadow-lg shadow-indigo-500/20 active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[15px]">check</span>
                            Submit ({entries.filter(e => e.name.trim()).length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Keyframe animations */}
            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
        </div>
    );
}
